import { FC, useMemo } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { Loader2 } from "lucide-react"

/**
 * Renders the agent's ```chart fenced blocks (see bb-admin-agent's system
 * prompt for the spec — one shared contract across web and mobile):
 *   { type: "line"|"bar"|"pie", title?, xLabels?: string[],
 *     series: [{ name?, data: number[] }] }
 * Invalid/unknown specs fall back to a plain code block; while the block is
 * still streaming in, a placeholder is shown instead of flickering JSON.
 */

interface ChartSpec {
  type: "line" | "bar" | "pie"
  title?: string
  xLabels?: string[]
  series: { name?: string; data: number[] }[]
}

const PALETTE = ["#0d9488", "#7c3aed", "#d97706", "#0284c7", "#e11d48"]

function parseSpec(source: string): ChartSpec | null {
  let raw: any
  try {
    raw = JSON.parse(source)
  } catch {
    return null
  }
  if (!raw || !["line", "bar", "pie"].includes(raw.type)) return null
  if (
    !Array.isArray(raw.series) ||
    raw.series.length === 0 ||
    raw.series.length > 5 ||
    !raw.series.every(
      (s: any) =>
        s &&
        Array.isArray(s.data) &&
        s.data.length > 0 &&
        s.data.length <= 100 &&
        s.data.every((v: any) => typeof v === "number" && isFinite(v))
    )
  ) {
    return null
  }
  return raw as ChartSpec
}

function toOptions(spec: ChartSpec): Highcharts.Options {
  const base: Highcharts.Options = {
    colors: PALETTE,
    chart: { height: 260, spacing: [12, 8, 8, 8] },
    title: {
      text: spec.title ?? "",
      style: { fontSize: "13px", fontWeight: "600" }
    },
    credits: { enabled: false },
    legend: { enabled: spec.series.length > 1, itemStyle: { fontSize: "11px" } }
  }
  if (spec.type === "pie") {
    return {
      ...base,
      chart: { ...base.chart, type: "pie" },
      series: [
        {
          type: "pie",
          data: (spec.xLabels ?? []).map((label, i) => ({
            name: label,
            y: spec.series[0].data[i] ?? 0
          })),
          dataLabels: { style: { fontSize: "11px" } }
        }
      ]
    }
  }
  return {
    ...base,
    // The model's "bar" means vertical bars — Highcharts calls that column
    chart: { ...base.chart, type: spec.type === "bar" ? "column" : "line" },
    xAxis: {
      categories: spec.xLabels,
      labels: { style: { fontSize: "10px" } }
    },
    yAxis: { title: { text: null } },
    series: spec.series.map(s => ({
      type: (spec.type === "bar" ? "column" : "line") as any,
      name: s.name ?? "",
      data: s.data
    }))
  }
}

const ChartBlock: FC<{ source: string; isStreaming: boolean }> = ({
  source,
  isStreaming
}) => {
  const spec = useMemo(() => parseSpec(source.trim()), [source])

  if (!spec) {
    if (isStreaming) {
      return (
        <div className="my-2 flex h-24 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-neutral-400">
          <Loader2 size={14} className="animate-spin" /> Building chart…
        </div>
      )
    }
    // Not streaming and still unparseable — show the raw block for debugging
    return (
      <pre className="overflow-x-auto rounded-lg bg-neutral-200 p-3 text-xs">
        {source}
      </pre>
    )
  }

  return (
    <div className="my-2 rounded-lg border border-gray-200 bg-white p-1">
      <HighchartsReact highcharts={Highcharts} options={toOptions(spec)} />
    </div>
  )
}

export default ChartBlock
